import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    CircularProgress,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { request } from "app/network";
import { formatLocalDateTime } from "utils/dateFormatter";
import { useQueryClient } from "@tanstack/react-query";

// ── 상태 코드 → Chip color 매핑
const STATUS_COLOR_MAP: Record<string, "warning" | "info" | "success" | "default"> = {
    INQUIRY_PENDING: "warning",
    INQUIRY_IN_PROGRESS: "info",
    INQUIRY_DONE: "success",
    INQUIRY_CLOSED: "default",
};

// ── 카테고리 라벨 fallback
const CATEGORY_LABELS: Record<string, string> = {
    INQUIRY_ACCOUNT_DELETE: "계정 삭제 요청",
    INQUIRY_POINT: "포인트 문의",
    INQUIRY_APP: "앱 오류/버그",
    INQUIRY_ETC: "기타",
};

// ── 타입 ──────────────────────────────────────────────────
interface InquiryItem {
    seqId: number;
    categoryCode: string;
    categoryName?: string;
    title: string;
    content?: string;
    name?: string;
    phone?: string;
    email?: string;
    statusCode: string;
    statusName?: string;
    answeredAt?: string;
    crtDt?: string;
}

interface CommonCodeItem {
    code: string;
    name: string;
    sortOrder?: number;
}

interface Props {
    open: boolean;
    item: InquiryItem | null;
    onClose: () => void;
}

// ── 정보 행 컴포넌트 ──────────────────────────────────────
const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <MDBox display="flex" alignItems="flex-start" gap={1} mb={1.2}>
        <MDTypography
            variant="caption"
            fontWeight="bold"
            color="text"
            sx={{ minWidth: 80, opacity: 0.65, mt: "1px" }}
        >
            {label}
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ flex: 1, wordBreak: "break-word" }}>
            {children}
        </MDTypography>
    </MDBox>
);

// ── 메인 컴포넌트 ─────────────────────────────────────────
const InquiryDetailModal: React.FC<Props> = ({ open, item, onClose }) => {
    const queryClient = useQueryClient();
    const [statusCode, setStatusCode] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // 공통코드 API에서 INQUIRY_STATUS 목록 조회
    const [statusOptions, setStatusOptions] = useState<CommonCodeItem[]>([]);
    useEffect(() => {
        const fetchStatusCodes = async () => {
            try {
                const response = await request("CommonCode.getByGroup", {
                    pathParams: { groupCode: "INQUIRY_STATUS" }
                });
                setStatusOptions(response.data ?? []);
            } catch (e) {
                console.error("INQUIRY_STATUS 공통코드 로드 실패:", e);
            }
        };
        fetchStatusCodes();
    }, []);

    // 모달 열릴 때 현재 상태로 초기화
    useEffect(() => {
        if (item) {
            setStatusCode(item.statusCode ?? "INQUIRY_PENDING");
            setError(null);
            setSuccess(false);
        }
    }, [item]);

    const currentStatusItem = statusOptions.find(o => o.code === (item?.statusCode ?? ""));
    const selectedStatusItem = statusOptions.find(o => o.code === statusCode);
    const isDirty = item && statusCode !== item.statusCode;

    const getChipColor = (code: string) =>
        STATUS_COLOR_MAP[code] ?? "default";

    const handleStatusChange = (e: SelectChangeEvent) => {
        setStatusCode(e.target.value);
        setError(null);
        setSuccess(false);
    };

    const handleSave = async () => {
        if (!item || !isDirty) return;
        setLoading(true);
        setError(null);
        setSuccess(false);
        try {
            await request("Inquiry.updateStatus", {
                pathParams: { seqId: String(item.seqId) },
                paramz: { statusCode },
            });
            setSuccess(true);
            queryClient.invalidateQueries({ queryKey: ["Inquiry.pagedList"] });
            setTimeout(() => onClose(), 800);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "상태 변경에 실패했습니다. 다시 시도해 주세요.");
        } finally {
            setLoading(false);
        }
    };

    if (!item) return null;

    const currentColor = getChipColor(item.statusCode);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            {/* 제목 */}
            <DialogTitle sx={{ pb: 1 }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon fontSize="medium" sx={{ color: "info.main" }}>support_agent</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                            문의 상세
                        </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                            #{item.seqId}
                        </MDTypography>
                        <Chip
                            label={currentStatusItem?.name ?? item.statusName ?? item.statusCode}
                            color={currentColor}
                            size="small"
                            sx={{
                                fontWeight: "bold",
                                ...((currentColor === "warning" || currentColor === "info" || currentColor === "success") && {
                                    color: "#fff",
                                    "& .MuiChip-label": { color: "#fff" },
                                }),
                            }}
                        />
                    </MDBox>
                </MDBox>
            </DialogTitle>

            <Divider sx={{ mx: 2, mb: 0 }} />

            <DialogContent sx={{ pt: 2 }}>

                {/* 기본 정보 */}
                <MDBox mb={2}>
                    <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase" sx={{ letterSpacing: 1, opacity: 0.7 }}>
                        기본 정보
                    </MDTypography>
                    <MDBox mt={1} pl={0.5}>
                        <InfoRow label="유형">
                            {item.categoryName ?? CATEGORY_LABELS[item.categoryCode] ?? item.categoryCode}
                        </InfoRow>
                        <InfoRow label="등록일">
                            {item.crtDt ? formatLocalDateTime(item.crtDt) : "-"}
                        </InfoRow>
                        {item.answeredAt && (
                            <InfoRow label="처리일">
                                {formatLocalDateTime(item.answeredAt)}
                            </InfoRow>
                        )}
                    </MDBox>
                </MDBox>

                <Divider sx={{ my: 1.5 }} />

                {/* 문의 내용 */}
                <MDBox mb={2}>
                    <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase" sx={{ letterSpacing: 1, opacity: 0.7 }}>
                        문의 내용
                    </MDTypography>
                    <MDBox mt={1} pl={0.5}>
                        <MDTypography variant="body2" fontWeight="bold" color="text" mb={0.5}>
                            {item.title}
                        </MDTypography>
                        {item.content ? (
                            <MDBox
                                sx={{
                                    background: "rgba(0,0,0,0.04)",
                                    borderRadius: 2,
                                    p: 1.5,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                <MDTypography variant="caption" color="text">
                                    {item.content}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                                (내용 없음)
                            </MDTypography>
                        )}
                    </MDBox>
                </MDBox>

                <Divider sx={{ my: 1.5 }} />

                {/* 문의자 정보 */}
                <MDBox mb={2}>
                    <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase" sx={{ letterSpacing: 1, opacity: 0.7 }}>
                        문의자 정보
                    </MDTypography>
                    <MDBox mt={1} pl={0.5}>
                        <InfoRow label="이름">{item.name || "-"}</InfoRow>
                        <InfoRow label="전화번호">{item.phone ? `📞 ${item.phone}` : "-"}</InfoRow>
                        <InfoRow label="이메일">{item.email ? `✉ ${item.email}` : "-"}</InfoRow>
                    </MDBox>
                </MDBox>

                <Divider sx={{ my: 1.5 }} />

                {/* 처리 상태 변경 */}
                <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase" sx={{ letterSpacing: 1, opacity: 0.7 }}>
                        처리 상태 변경
                    </MDTypography>
                    <MDBox mt={1.5} display="flex" alignItems="center" gap={2}>
                        <FormControl size="medium" sx={{ minWidth: 200 }}>
                            <InputLabel>처리 상태</InputLabel>
                            <Select
                                value={statusCode}
                                label="처리 상태"
                                onChange={handleStatusChange}
                                disabled={loading}
                                sx={{ height: 44 }}
                            >
                                {statusOptions.map(opt => {
                                    const chipColor = getChipColor(opt.code);
                                    return (
                                        <MenuItem key={opt.code} value={opt.code}>
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <Chip
                                                    label={opt.name}
                                                    color={chipColor}
                                                    size="small"
                                                    sx={{
                                                        fontSize: 11,
                                                        height: 20,
                                                        ...((chipColor === "warning" || chipColor === "info" || chipColor === "success") && {
                                                            color: "#fff",
                                                            "& .MuiChip-label": { color: "#fff" },
                                                        }),
                                                    }}
                                                />
                                            </MDBox>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        {selectedStatusItem && statusCode !== item.statusCode && (
                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                                {currentStatusItem?.name ?? item.statusCode} → {selectedStatusItem.name}
                            </MDTypography>
                        )}
                    </MDBox>

                    {/* 결과 메시지 */}
                    {error && (
                        <MDBox mt={1.5} p={1.5} sx={{ background: "#FEF2F2", borderRadius: 2, border: "1px solid #FECACA" }}>
                            <MDTypography variant="caption" sx={{ color: "#DC2626" }}>
                                ⚠️ {error}
                            </MDTypography>
                        </MDBox>
                    )}
                    {success && (
                        <MDBox mt={1.5} p={1.5} sx={{ background: "#F0FDF4", borderRadius: 2, border: "1px solid #BBF7D0" }}>
                            <MDTypography variant="caption" sx={{ color: "#16A34A" }}>
                                ✅ 상태가 변경되었습니다.
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>

            <Divider sx={{ mx: 2 }} />

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
                    닫기
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSave}
                    disabled={!isDirty || loading}
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default InquiryDetailModal;
